<template>
    <div class="dashboard-container pa-4">
        <v-row>
            <!-- Left Column -->
            <v-col cols="12" :md="8" class="pr-md-6">
                <!-- Profile Overview Card -->
                <v-card class="profile-card mb-6" elevation="0">
                    <v-card-text class="pa-6">
                        <!-- Greeting Section -->
                        <div :class="['greeting-section mb-6', { 'greeting-section--graduated': isGraduated }]">
                            <div class="greeting-text">
                                {{ greeting }}, <span class="greeting-name">{{ student?.name?.split(' ')[0] || 'Student'
                                }}</span>
                                <span v-if="isGraduated" class="graduation-icon">&#127891;</span>
                            </div>
                            <div class="greeting-subtext">{{ greetingMessage }}</div>
                        </div>

                        <div>
                            <v-alert type="error" class="mt-2"
                                v-if="getUser.programme != null && !getUser.detailsCorrect">
                                Consider having your admission details updated!!!
                            </v-alert>
                            <v-alert type="warning" class="mt-2"
                                v-if="student && student.studentStatus === 'GRADUATED'">
                                Please update your National Identification Number (NIN) under
                                <strong><router-link to="/biodata">Biodata &rarr; Identity</router-link></strong>.
                            </v-alert>
                        </div>
                        <div class="d-flex flex-wrap align-start">
                            <!-- Profile Image -->
                            <div class="profile-image-container mr-6 mb-6">
                                <v-avatar size="120" class="profile-avatar">
                                    <v-img :src="image" :aspect-ratio="1"></v-img>
                                </v-avatar>
                                <div class="mt-4 text-center">
                                    <div class="text-h6 font-weight-bold">{{ student?.name }}</div>
                                    <div class="text-caption grey--text">{{ student?.userId }}</div>
                                </div>
                            </div>

                            <!-- Quick Stats -->
                            <div class="flex-grow-1">
                                <div class="stats-grid">
                                    <div class="stat-item">
                                        <div class="stat-label">Level</div>
                                        <div class="stat-value">{{ student?.level?.title ?? 'None' }}</div>
                                    </div>
                                    <div class="stat-item">
                                        <div class="stat-label">Programme</div>
                                        <div class="stat-value">{{ student?.programme.programmeType.name }}</div>
                                    </div>
                                    <div class="stat-item">
                                        <div class="stat-label">Session</div>
                                        <div class="stat-value">{{ (student?.lastLevelUpdateSession != null) ?
                                            student.lastLevelUpdateSession.name : "Unset" }}</div>
                                    </div>
                                    <div class="stat-item">
                                        <div class="stat-label">Semester</div>
                                        <div class="stat-value">{{ currentSemester }}</div>
                                    </div>
                                    <div class="stat-item" v-if="student?.studentStatus">
                                        <div class="stat-label">Status</div>
                                        <div class="stat-value">
                                            <v-chip small :color="studentStatusColor" text-color="white"
                                                class="status-chip">
                                                {{ student.studentStatus }}
                                            </v-chip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Student Details Card -->
                <v-card elevation="0" class="details-card">
                    <v-card-text class="pa-6">
                        <div class="d-flex justify-space-between align-center mb-6">
                            <div class="text-h6 font-weight-medium">Student Information</div>
                            <v-chip
                                v-if="getUser?.programme != null && getUser.programme.programmeType.id !== getUser.yearOfAdmission.programmeType.id"
                                color="error" small class="px-2">
                                Update Required
                            </v-chip>
                        </div>

                        <!-- Information Grid -->
                        <v-row>
                            <v-col cols="12" md="6">
                                <div class="info-section">
                                    <div class="section-title">Personal Details</div>
                                    <div class="info-list">
                                        <div class="info-item" v-for="(item, index) in personalInfo" :key="index">
                                            <div class="info-label">{{ item.label }}</div>
                                            <div class="info-value">{{ item.value }}</div>
                                        </div>
                                    </div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="6">
                                <div class="info-section">
                                    <div class="section-title">Academic Details</div>
                                    <div class="info-list">
                                        <div class="info-item" v-for="(item, index) in academicInfo" :key="index">
                                            <div class="info-label">{{ item.label }}</div>
                                            <div class="info-value">{{ item.value }}</div>
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Right Column - Status Cards -->
            <v-col cols="12" :md="4">
                <!-- Payment Status Card -->
                <v-card class="status-card mb-6" elevation="0">
                    <v-card-text class="pa-6">
                        <div class="d-flex align-center mb-6">
                            <div class="status-icon payment-icon">
                                <v-icon>mdi-cash-multiple</v-icon>
                            </div>
                            <div class="ml-4">
                                <div class="text-h6 font-weight-medium">Payment Status</div>
                                <div class="text-caption grey--text">Session payments overview</div>
                            </div>
                        </div>

                        <div class="payment-list">
                            <div v-for="(item, index) in dashboard?.payments || []" :key="index" class="payment-item">
                                <div class="d-flex align-center justify-space-between">
                                    <span class="payment-session">{{ item.session.name }}</span>
                                    <v-chip small :color="item.cleared ? 'success' : 'error'" text-color="white">
                                        {{ item.cleared ? 'Paid' : 'Pending' }}
                                    </v-chip>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Progress Card -->
                <v-card v-if="getUser?.id && getUser.programme.programmeType.enableProgressStatus" class="status-card"
                    elevation="0">
                    <v-card-text class="pa-6">
                        <div class="d-flex align-center mb-6">
                            <div class="status-icon progress-icon">
                                <v-icon>mdi-chart-timeline-variant</v-icon>
                            </div>
                            <div class="ml-4">
                                <div class="text-h6 font-weight-medium">Academic Progress</div>
                                <div class="text-caption grey--text">Current standing</div>
                            </div>
                        </div>
                        <progress-status :user="getUser"></progress-status>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <loading-dialog :show="loading"></loading-dialog>
        <message-box :show="messageShow" :message="message" :type="messageType"
            @close-message="messageShow = !messageShow"></message-box>
    </div>
</template>
<script>
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import { mapGetters } from "vuex";
import Defaults from "@/shared/Defaults";
import ErrorResolver from "@/shared/ErrorResolver";
import Api from "@/main/api";
import ProgressStatus from "@/student/components/ProgressStatus.vue";
export default {
    name: "StudentDashboard",
    components: { ProgressStatus, MessageBox, LoadingDialog },
    data: () => ({
        messageType: '',
        message: '',
        messageShow: false,
        loading: false,
        student: null,
        image: '',
        dashboard: null,
    }),

    methods: {
        fetchAll() {
            this.loading = true
            Api.user.dashboard().then(res => {
                this.dashboard = res.data
            })
                .catch(err => ErrorResolver.checkErrors(this, err))
                .finally(() => this.loading = false)
        },
        navigateToHostel() {
            this.$router.push({ path: '/accommodation' });
        }
    },

    computed: {
        ...mapGetters(['getUser', 'getCurrentSession']),
        isLargeAndUp: function () {
            return this.$vuetify.breakpoint.lgAndUp
        },

        isMobile: function () {
            return this.$vuetify.breakpoint.smAndDown
        },

        hasLoaded: function () {
            return this.student != null && this.dashboard != null && this.student.temporal === undefined
        },

        currentSemester: function () {
            if (this.student == null || this.student.lastLevelUpdateSession == null)
                return ''

            let semester = null
            semester = this.student.lastLevelUpdateSession.semesters?.find(e => e.open)

            if (!semester) {
                for (let i = 0; i < this.student.lastLevelUpdateSession.semesters.length; i++) {
                    semester = this.student.lastLevelUpdateSession.semesters[i]
                }
            }

            if (semester == null) {
                return 'None'
            }

            return semester.title ?? 'None'
        },

        personalInfo() {
            return [
                // { label: 'Full Name', value: this.student?.name },
                // { label: 'Matric Number', value: this.student?.userId },
                { label: 'Application ID', value: this.student?.registrationNumber },
                { label: 'Phone Number', value: this.student?.phone },
                { label: 'Institutional Email', value: this.student?.institutionalEmail || 'Not available' }
            ];
        },

        academicInfo() {
            return [
                // { label: 'Current Level', value: this.student?.level.title },
                { label: 'Year of Admission', value: this.student?.yearOfAdmission.name },
                // { label: 'Programme Type', value: this.student?.programme.programmeType.name },
                { label: 'Faculty', value: this.student?.programme.department.faculty.name },
                // { label: 'Department', value: this.student?.programme.department.name },
                { label: 'Programme', value: this.student?.programme.name }
            ];
        },

        greeting() {
            const hour = new Date().getHours();
            if (hour >= 5 && hour < 12) {
                return 'Good Morning';
            } else if (hour >= 12 && hour < 17) {
                return 'Good Afternoon';
            } else {
                return 'Good Evening';
            }
        },

        studentStatusColor() {
            const status = this.student?.studentStatus?.toUpperCase();
            switch (status) {
                case 'ACTIVE':
                    return 'success';
                case 'GRADUATED':
                    return 'primary';
                case 'SUSPENDED':
                case 'EXPELLED':
                    return 'error';
                case 'DEFERRED':
                case 'WITHDRAWN':
                    return 'warning';
                default:
                    return 'grey';
            }
        },

        isGraduated() {
            return this.student?.studentStatus?.toUpperCase() === 'GRADUATED';
        },

        greetingMessage() {
            const graduatedMessages = [
                'Congratulations on your graduation!',
                'Welcome back, proud alumnus!',
                'Your hard work has paid off.',
                'You did it — you\'re a graduate!',
                'The world awaits your expertise.',
                'Your alma mater is proud of you.',
                'A new chapter begins after graduation.',
                'Your degree is a testament to your dedication.',
                'Welcome to the alumni community!',
                'Go out and make a difference.',
                'Your graduation is just the beginning.',
                'Carry your knowledge forward with pride.',
            ];

            const messages = [
                'Time to ace those lectures today!',
                'Your academic journey continues.',
                'Ready to tackle your coursework?',
                'Another day of learning awaits.',
                'Stay on top of your assignments.',
                'Knowledge is built one lecture at a time.',
                'Check your course materials and deadlines.',
                'Academic excellence starts with consistency.',
                'Time to review your study schedule.',
                'Keep your academic goals in focus.',
                'Your degree is worth the effort.',
                'Stay committed to your studies.',
                'Review your notes and prepare for classes.',
                'Academic success requires daily dedication.',
                'Check for new announcements and updates.',
                'Your professors believe in your potential.',
                'Time to engage with your course content.',
                'Every assignment builds your expertise.',
                'Stay organized with your academic calendar.',
                'Your GPA reflects your daily efforts.',
                'Prepare well for upcoming assessments.',
                'Research and reading strengthen your mind.',
                'Academic integrity is your foundation.',
                'Connect with classmates and study groups.',
                'Your thesis/project needs your attention.',
                'Library resources are waiting for you.',
                'Time to complete pending assignments.',
                'Academic deadlines require careful planning.',
                'Your academic transcript tells your story.',
                'Stay curious about your field of study.',
                'Attend lectures and take detailed notes.',
                'Your academic advisor is here to help.',
                'Exam preparation starts today.',
                'Participate actively in class discussions.',
                'Your research skills grow with practice.',
                'Academic conferences broaden your horizons.',
                'Complete your course evaluations honestly.',
                'Time to update your academic portfolio.',
                'Learning never stops, embrace it.',
                'Consistent effort leads to academic success.'
            ];

            const pool = this.isGraduated ? graduatedMessages : messages;

            // Generate a consistent random index based on the day to keep same message throughout the day
            const today = new Date().toDateString();
            let hash = 0;
            for (let i = 0; i < today.length; i++) {
                hash = ((hash << 5) - hash) + today.charCodeAt(i);
                hash = hash & hash;
            }
            const index = Math.abs(hash) % pool.length;
            return pool[index];
        },
    },

    watch: {
        getUser: function (newVal) {
            this.student = newVal
            if (newVal != null)
                this.image = Defaults.api + '/profile-picture/fetch?user=' + newVal.userId;


            this.fetchAll()
        }
    },

    created() {
        // get dashboard
        this.fetchAll()
        this.student = this.getUser
        this.image = Defaults.api + '/profile-picture/fetch?user=' + this.getUser.userId;
    }
}
</script>

<style lang="scss" scoped>
.dashboard-container {
    background-color: #f8fafc;
    min-height: calc(100vh - 64px);
}

// Cards
.profile-card,
.details-card,
.status-card {
    background: white;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    }
}

// Greeting Section
.greeting-section {
    padding: 20px 24px;
    background: linear-gradient(135deg, #016b4b 0%, #018a61 100%);
    border-radius: 12px;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -10%;
        width: 200px;
        height: 200px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
    }

    .greeting-text {
        font-size: 1.5rem;
        font-weight: 600;
        color: white;
        margin-bottom: 4px;
        position: relative;
        z-index: 1;

        .greeting-name {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            font-weight: 700;
        }
    }

    .greeting-subtext {
        font-size: 0.9rem;
        color: rgba(255, 255, 255, 0.9);
        position: relative;
        z-index: 1;
    }

    &--graduated {
        background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);

        &::before {
            background: rgba(255, 215, 0, 0.15);
        }

        .greeting-name {
            background: linear-gradient(135deg, #ffd700 0%, #ffecb3 50%, #ffd700 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .graduation-icon {
            font-size: 1.3rem;
            margin-left: 4px;
        }
    }
}

// Profile Section
.profile-avatar {
    border: 4px solid white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

// Stats Grid
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 16px;
}

.stat-item {
    padding: 16px;
    background: #f8fafc;
    border-radius: 12px;

    .stat-label {
        font-size: 0.75rem;
        color: #64748b;
        margin-bottom: 4px;
    }

    .stat-value {
        font-size: 0.9rem;
        font-weight: 600;
        color: #1e293b;
    }
}

// Information Sections
.info-section {
    .section-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: #64748b;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
}

.info-list {
    .info-item {
        padding: 8px 0;
        border-bottom: 1px solid #f1f5f9;

        &:last-child {
            border-bottom: none;
        }

        .info-label {
            font-size: 0.875rem;
            color: #64748b;
            margin-bottom: 4px;
        }

        .info-value {
            font-size: 0.9rem;
            color: #1e293b;
            font-weight: 500;
        }
    }
}

// Status Icons
.status-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.payment-icon {
        background: linear-gradient(135deg, #016b4b20 0%, #016b4b40 100%);
        color: #016b4b;
    }

    &.progress-icon {
        background: linear-gradient(135deg, #0284c720 0%, #0284c740 100%);
        color: #0284c7;
    }
}

// Payment List
.payment-list {
    .payment-item {
        padding: 12px 0;
        border-bottom: 1px solid #f1f5f9;

        &:last-child {
            border-bottom: none;
        }

        .payment-session {
            font-size: 0.9rem;
            color: #1e293b;
            font-weight: 500;
        }
    }
}

.status-chip {
    font-size: 0.75rem !important;
    height: 24px !important;
}

// Responsive Design
@media (max-width: 960px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .profile-image-container {
        width: 100%;
        text-align: center;
        margin-right: 0;
        margin-bottom: 24px;
    }

    .greeting-section {
        padding: 16px 20px;

        .greeting-text {
            font-size: 1.25rem;
        }

        .greeting-subtext {
            font-size: 0.85rem;
        }
    }
}
</style>
